<!-- Responsibility: Self-managing system bar for global status and actions.
Scope: Top-level persistent bar; no props, uses dispatcher and Managers. -->
<system-bar>
  <header class="system-bar">
    <div class="left-group">
      <span id="log-text">{ state.logText }</span>
      <span id="bg-task-text" if={ state.bgTaskText }>· { state.bgTaskText }</span>
      <span id="ai-provider-text" class="ai-provider-badge">{ state.aiProviderLabel }</span>
      <span if={ state.quotaRemaining != null } class="quota-badge">QUOTA: { state.quotaRemaining }</span>
      <span class="net-badge offline" if={ state.isOffline }>OFFLINE MODE</span>
    </div>
    
    <div class="right-group">
      <button class="audio-toggle sbar-icon-btn tooltip-wrap" 
        data-tip="Toggle audio"
        data-active={ state.audioEnabled ? 'true' : 'false' } 
        onclick={ toggleAudio } 
        aria-label="Toggle audio">
        <span class="sbar-icon-label">{ state.audioEnabled ? '🔊' : '🔇' }</span>
      </button>

      <button
        if={ state.ttsSupported }
        class="audio-toggle sbar-icon-btn tooltip-wrap"
        data-tip={ state.ttsEnabled ? 'Narration voice ON' : 'Narration voice OFF' }
        data-active={ state.ttsEnabled ? 'true' : 'false' }
        onclick={ toggleTts }
        aria-label="Toggle narration voice"
      >
        <span class="sbar-icon-label">TTS</span>
      </button>

      <button
        if={ state.isAdmin }
        id="btn-dev"
        class="dev-toggle tooltip-wrap { state.isDev ? 'active' : '' }"
        data-tip={
          state.isDev
            ? 'Dev mode ON: advanced diagnostics UI is enabled.'
            : 'Dev mode OFF: advanced diagnostics UI is hidden.'
        }
        onclick={ toggleDev }
      >
        DEV [{ state.isDev ? 'ON' : 'OFF' }]
      </button>

      <button id="btn-exit-game" class="exit-btn sbar-icon-btn" 
        if={ state.currentPage === 'scene-view' } 
        onclick={ exitToHub } 
        aria-label="EXIT TO HUB">
        <span class="sbar-icon-label sbar-exit-label">← EXIT TO HUB</span>
      </button>

      <account-menu
        view={ accountView() }
        actions={ accountActions() }
      ></account-menu>
    </div>
  </header>

  <script>
    import { dispatcher, setDevMode, navigateState, stateService } from '../../app-states.js'
    import { TOPICS } from "@anyquest/shared-core"
    import { sessionManager } from '../../session.js'
    import { initAudio, toggleAudio, isAudioEnabled, playEffect, stopAmbient } from '../../engine/audio.js'
    import { isTtsSupported, isTtsEnabled, toggleTts, stopNarration } from '../../engine/tts.js'
    import { router as aiRouter } from '../../ai/router.js'
    import * as gameStore from '../../game-store.js'
    import { persistLobbySection } from '../lobby/lobby-state.ts'
    import { applyPwaUpdate, requestPwaInstall } from '../../pwa.js'
    import AccountMenu from './account-menu.riot'

    export default {
      components: { AccountMenu },
      
      state: {
        logText: 'SYSTEM READY',
        bgTaskText: '',
        aiProviderLabel: 'AI · UNKNOWN',
        quotaRemaining: null,
        isOffline: !navigator.onLine,
        audioEnabled: isAudioEnabled(),
        ttsEnabled: isTtsEnabled(),
        ttsSupported: isTtsSupported(),
        isAdmin: false,
        pwaInstallReady: false,
        pwaUpdateReady: false,
        isDev: false,
        currentPage: 'login-view',
        session: null,
        profileOpen: false
      },

      onMounted() {
        this.syncRouting()

        this.handles = [
          dispatcher.on(TOPICS.ui.notify, (_o, d) => this.update({ logText: d?.message ?? '' })),
          dispatcher.on(TOPICS.auth.signedIn, () => this.update({ isAdmin: sessionManager.user.isAdmin })),
          dispatcher.on(TOPICS.system.pwaInstallable, () => this.update({ pwaInstallReady: true })),
          dispatcher.on(TOPICS.system.pwaUpdateReady, () => this.update({ pwaUpdateReady: true }))
        ]

        this.navHandle = stateService.onChange(() => this.syncRouting())
        
        this.providerUnsub = aiRouter.onActiveProviderChange((_id, label) => {
          this.update({ aiProviderLabel: `AI · ${label}` })
        })

        this.refreshProviderLabel()
        this.update()
      },

      onUnmounted() {
        if (this.handles) this.handles.forEach(h => dispatcher.off(h))
        if (this.navHandle) this.navHandle.off()
        if (this.providerUnsub) this.providerUnsub()
      },

      syncRouting() {
        const s = stateService.getState()
        this.update({
          currentPage: s?.pageName ?? 'login-view',
          isAdmin: sessionManager.user.isAdmin,
        })
      },

      refreshProviderLabel() {
        const active = aiRouter.getActiveProviderLabel()
        if (active) this.update({ aiProviderLabel: `AI · ${active}` })
      },

      accountView() {
        const u = sessionManager.user
        return {
          photoURL: u.photoURL,
          initials: u.initials,
          displayName: u.displayName,
          email: u.email,
          isAdmin: u.isAdmin,
          isDev: this.state.isDev,
          pwaInstallReady: this.state.pwaInstallReady,
          pwaUpdateReady: this.state.pwaUpdateReady,
          profileOpen: this.state.profileOpen,
        }
      },

      accountActions() {
        return {
          toggleProfileDropdown: () => {
            playEffect('ui_click')
            this.update({ profileOpen: !this.state.profileOpen })
          },
          signOut: () => {
            this.update({ profileOpen: false })
            sessionManager.logout()
          },
          toggleDev: () => {
            this.update({ profileOpen: false })
            this.toggleDev()
          },
          installPwa: () => {
            this.update({ profileOpen: false })
            requestPwaInstall()
          },
          applyPwaUpdateNow: () => applyPwaUpdate(),
          openAdminDashboardPage: () => {
            this.update({ profileOpen: false })
            if (this.state.currentPage === 'scene-view' && typeof window !== 'undefined') {
              window.open(new URL('/admin', window.location.origin).toString(), '_blank', 'noopener')
              return
            }
            navigateState('admin')
          },
          openWelcomePage: () => {
            this.update({ profileOpen: false })
            try {
              window.sessionStorage.setItem('am2_welcome_debug', '1')
            } catch {}
            navigateState('welcome')
          },
          openSettingsModal: (tab) => {
            this.update({ profileOpen: false })
            dispatcher.trigger('app', TOPICS.ui.dialogOpen, { content: 'settings-dialog', data: { initialTab: tab } })
          },
          openSystemControlsModal: () => {
            this.update({ profileOpen: false })
            dispatcher.trigger('app', TOPICS.ui.dialogOpen, { content: 'system-controls-dialog', data: {} })
          },
          openAboutModal: () => {
            this.update({ profileOpen: false })
            dispatcher.trigger('app', TOPICS.ui.dialogOpen, { content: 'about-dialog', data: {} })
          },
          openDevObservabilityPage: () => {
            this.update({ profileOpen: false })
            navigateState('dev')
          },
          switchAccount: async () => {
            this.update({ profileOpen: false })
            await sessionManager.switchAccount?.()
          },
        }
      },

      toggleAudio() {
        initAudio()
        const audioEnabled = toggleAudio()
        this.update({ audioEnabled })
        if (audioEnabled) playEffect('ui_open')
      },

      toggleTts() {
        this.update({ ttsEnabled: toggleTts() })
      },

      toggleDev() {
        if (!sessionManager.user.isAdmin) return
        const next = !this.state.isDev
        setDevMode(next)
        this.update({ isDev: next })
        playEffect('ui_click')
      },

      async exitToHub() {
        stopNarration(true)
        stopAmbient()
        await gameStore.save()
        persistLobbySection('hub')
        navigateState('hub')
      }
    }
  </script>
</system-bar>
